export type AppetiteTolerance = "Low" | "Medium" | "High";
export type AppetiteMetricStatus = "Within" | "EWI" | "At Limit" | "Recovery" | "Capacity" | "Pending";
export type WorkflowState = "draft" | "under_review" | "approved" | "superseded";
export type MetricComparator = "min" | "max";
export type MetricTrend = "up" | "down" | "flat";

export interface RiskAppetiteCategory {
  id: string;
  title: string;
  statement: string;
  tolerance: AppetiteTolerance;
}

export interface RiskAppetiteThreshold {
  value: number | null;
  label: string;
}

export interface RiskAppetiteMetric {
  id: string;
  categoryId: string;
  sequence: number;
  metric: string;
  comparator: MetricComparator;
  unit: "percent" | "days" | "count" | "gbp_million_buffer";
  thresholds: {
    level1: RiskAppetiteThreshold;
    level2: RiskAppetiteThreshold;
    level3: RiskAppetiteThreshold;
    level4: RiskAppetiteThreshold;
  };
  actualValue: number | null;
  trend: MetricTrend;
  lastUpdated: string;
  line1Owner: string;
  line2Owner: string;
  line3Assurance: string;
  note?: string;
}

export interface RiskAppetiteObservation {
  id: string;
  metricId: string;
  previousValue: number | null;
  newValue: number | null;
  comment: string;
  updatedBy: string;
  updatedAt: string;
}

export interface RiskAppetiteException {
  id: string;
  metricId: string;
  reason: string;
  approvedBy: string;
  expiresAt: string;
  createdAt: string;
  status: "Open" | "Closed";
}

export interface RiskAppetiteWorkflowEvent {
  id: string;
  action: "created" | "submit_for_review" | "approved" | "request_changes" | "temporary_breach_accepted";
  actor: string;
  at: string;
  comment: string;
}

export interface RiskAppetiteProfile {
  id: string;
  name: string;
  workflowState: WorkflowState;
  simulationMode: true;
  effectiveFrom: string;
  nextAnnualReviewDate: string;
  boardCommittee: string;
  barcCommittee: string;
  ownerLine1: string;
  ownerLine2: string;
  ownerLine3: string;
}

export const INITIAL_PROFILE: RiskAppetiteProfile = {
  id: "ras-2026-v1",
  name: "Risk Appetite Statement 2026 (Static Demo)",
  workflowState: "draft",
  simulationMode: true,
  effectiveFrom: "2026-01-01",
  nextAnnualReviewDate: "2026-12-31",
  boardCommittee: "Board Risk Committee",
  barcCommittee: "BARC",
  ownerLine1: "Treasury and Balance Sheet Management",
  ownerLine2: "Risk and Compliance",
  ownerLine3: "Internal Audit",
};

export const INITIAL_CATEGORIES: RiskAppetiteCategory[] = [
  {
    id: "balance-sheet-management-risk",
    title: "Balance Sheet Management Risk",
    statement:
      "The organisation has low appetite for funding and liquidity deterioration and maintains prudent internal buffers above regulatory minimums.",
    tolerance: "Low",
  },
  {
    id: "credit-risk",
    title: "Credit Risk",
    statement:
      "Credit risk is central to strategy and accepted only when consciously taken, fully understood, and adequately compensated.",
    tolerance: "Medium",
  },
  {
    id: "operational-risk",
    title: "Operational Risk",
    statement:
      "There is no appetite for material operational losses; minor incidents must be mitigated quickly through controls and monitoring.",
    tolerance: "Low",
  },
  {
    id: "compliance-risk",
    title: "Compliance Risk",
    statement:
      "There is very limited appetite for non-compliance and regulatory breaches, with strong emphasis on preventative controls.",
    tolerance: "Low",
  },
];

export const INITIAL_METRICS: RiskAppetiteMetric[] = [
  {
    id: "bsmr-1",
    categoryId: "balance-sheet-management-risk",
    sequence: 1,
    metric: "CET Ratio *",
    comparator: "min",
    unit: "gbp_million_buffer",
    thresholds: {
      level1: { value: 3, label: "TCR + buffers + GBP3m" },
      level2: { value: 2, label: "TCR + buffers + GBP2m" },
      level3: { value: 1, label: "TCR + buffers + GBP1m" },
      level4: { value: 0, label: "TCR + buffers" },
    },
    actualValue: 2.4,
    trend: "down",
    lastUpdated: "2026-03-04",
    line1Owner: "Treasury Lead",
    line2Owner: "CRO Office",
    line3Assurance: "Internal Audit",
  },
  {
    id: "bsmr-2",
    categoryId: "balance-sheet-management-risk",
    sequence: 2,
    metric: "Leverage Ratio",
    comparator: "min",
    unit: "percent",
    thresholds: {
      level1: { value: 7, label: "7.0%" },
      level2: { value: 5.5, label: "5.5%" },
      level3: { value: null, label: "-" },
      level4: { value: null, label: "-" },
    },
    actualValue: 5.9,
    trend: "flat",
    lastUpdated: "2026-03-04",
    line1Owner: "Treasury Lead",
    line2Owner: "Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "bsmr-3",
    categoryId: "balance-sheet-management-risk",
    sequence: 3,
    metric: "Liquidity Coverage Ratio (LCR)",
    comparator: "min",
    unit: "percent",
    thresholds: {
      level1: { value: 135, label: "135%" },
      level2: { value: 125, label: "125%" },
      level3: { value: 115, label: "115%" },
      level4: { value: 100, label: "100%" },
    },
    actualValue: 121,
    trend: "down",
    lastUpdated: "2026-03-05",
    line1Owner: "Liquidity Manager",
    line2Owner: "Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "bsmr-4",
    categoryId: "balance-sheet-management-risk",
    sequence: 4,
    metric: "Net Stable Funding Ratio",
    comparator: "min",
    unit: "percent",
    thresholds: {
      level1: { value: 120, label: "120%" },
      level2: { value: 110, label: "110%" },
      level3: { value: 105, label: "105%" },
      level4: { value: 100, label: "100%" },
    },
    actualValue: 108,
    trend: "down",
    lastUpdated: "2026-03-05",
    line1Owner: "Funding Manager",
    line2Owner: "Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "bsmr-5",
    categoryId: "balance-sheet-management-risk",
    sequence: 5,
    metric: "Survival Horizon",
    comparator: "min",
    unit: "days",
    thresholds: {
      level1: { value: 100, label: "100 Days" },
      level2: { value: 90, label: "90 Days" },
      level3: { value: 85, label: "85 Days" },
      level4: { value: 80, label: "80 Days" },
    },
    actualValue: 92,
    trend: "flat",
    lastUpdated: "2026-03-04",
    line1Owner: "Treasury Lead",
    line2Owner: "Liquidity Risk",
    line3Assurance: "Internal Audit",
  },
  {
    id: "bsmr-6",
    categoryId: "balance-sheet-management-risk",
    sequence: 6,
    metric: "Whole Sale Deposit/total deposit",
    comparator: "max",
    unit: "percent",
    thresholds: {
      level1: { value: 30, label: "30%" },
      level2: { value: 40, label: "40%" },
      level3: { value: null, label: "-" },
      level4: { value: null, label: "-" },
    },
    actualValue: 33,
    trend: "up",
    lastUpdated: "2026-03-05",
    line1Owner: "Funding Manager",
    line2Owner: "Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "bsmr-7",
    categoryId: "balance-sheet-management-risk",
    sequence: 7,
    metric: "Intra day liquidity",
    comparator: "min",
    unit: "count",
    thresholds: {
      level1: { value: null, label: "TBD" },
      level2: { value: null, label: "-" },
      level3: { value: null, label: "-" },
      level4: { value: null, label: "-" },
    },
    actualValue: null,
    trend: "flat",
    lastUpdated: "2026-03-01",
    line1Owner: "Treasury Operations",
    line2Owner: "Risk Oversight",
    line3Assurance: "Internal Audit",
    note: "Threshold definition pending during framework calibration.",
  },
  {
    id: "bsmr-8",
    categoryId: "balance-sheet-management-risk",
    sequence: 8,
    metric: "Loan to Deposit Ratio",
    comparator: "max",
    unit: "percent",
    thresholds: {
      level1: { value: 90, label: "90%" },
      level2: { value: 96, label: "96%" },
      level3: { value: null, label: "-" },
      level4: { value: null, label: "-" },
    },
    actualValue: 92,
    trend: "up",
    lastUpdated: "2026-03-03",
    line1Owner: "Treasury Lead",
    line2Owner: "ALCO Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "bsmr-9",
    categoryId: "balance-sheet-management-risk",
    sequence: 9,
    metric: "EVE shift under 250bp parallel shift",
    comparator: "max",
    unit: "percent",
    thresholds: {
      level1: { value: 13, label: "13%" },
      level2: { value: 13.5, label: "13.5%" },
      level3: { value: null, label: "-" },
      level4: { value: null, label: "-" },
    },
    actualValue: 12.8,
    trend: "flat",
    lastUpdated: "2026-03-02",
    line1Owner: "Treasury Analytics",
    line2Owner: "Market Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "bsmr-10",
    categoryId: "balance-sheet-management-risk",
    sequence: 10,
    metric: "LCR Breach of risk appetite within the next 6 months",
    comparator: "max",
    unit: "count",
    thresholds: {
      level1: { value: 1, label: "1" },
      level2: { value: null, label: "-" },
      level3: { value: null, label: "-" },
      level4: { value: null, label: "-" },
    },
    actualValue: 0,
    trend: "flat",
    lastUpdated: "2026-03-05",
    line1Owner: "Liquidity Manager",
    line2Owner: "Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "bsmr-11",
    categoryId: "balance-sheet-management-risk",
    sequence: 11,
    metric: "LCR Breach of risk appetite within the next 12 months",
    comparator: "max",
    unit: "count",
    thresholds: {
      level1: { value: 1, label: "1" },
      level2: { value: null, label: "-" },
      level3: { value: null, label: "-" },
      level4: { value: null, label: "-" },
    },
    actualValue: 0,
    trend: "flat",
    lastUpdated: "2026-03-05",
    line1Owner: "Liquidity Manager",
    line2Owner: "Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "cr-1",
    categoryId: "credit-risk",
    sequence: 1,
    metric: "Stage 3 Ratio",
    comparator: "max",
    unit: "percent",
    thresholds: {
      level1: { value: 4.5, label: "4.5%" },
      level2: { value: 5.5, label: "5.5%" },
      level3: { value: 6.5, label: "6.5%" },
      level4: { value: 7.5, label: "7.5%" },
    },
    actualValue: 4.9,
    trend: "up",
    lastUpdated: "2026-03-05",
    line1Owner: "Credit Portfolio Lead",
    line2Owner: "Credit Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "cr-2",
    categoryId: "credit-risk",
    sequence: 2,
    metric: "NPL Coverage Ratio",
    comparator: "min",
    unit: "percent",
    thresholds: {
      level1: { value: 60, label: "60%" },
      level2: { value: 55, label: "55%" },
      level3: { value: 50, label: "50%" },
      level4: { value: 45, label: "45%" },
    },
    actualValue: 57,
    trend: "down",
    lastUpdated: "2026-03-04",
    line1Owner: "Credit Portfolio Lead",
    line2Owner: "Credit Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "cr-3",
    categoryId: "credit-risk",
    sequence: 3,
    metric: "Top 20 Concentration",
    comparator: "max",
    unit: "percent",
    thresholds: {
      level1: { value: 28, label: "28%" },
      level2: { value: 32, label: "32%" },
      level3: { value: 36, label: "36%" },
      level4: { value: 40, label: "40%" },
    },
    actualValue: 27.3,
    trend: "flat",
    lastUpdated: "2026-03-03",
    line1Owner: "Credit Portfolio Lead",
    line2Owner: "Credit Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "or-1",
    categoryId: "operational-risk",
    sequence: 1,
    metric: "Critical Incidents (Monthly)",
    comparator: "max",
    unit: "count",
    thresholds: {
      level1: { value: 2, label: "2" },
      level2: { value: 3, label: "3" },
      level3: { value: 4, label: "4" },
      level4: { value: 5, label: "5" },
    },
    actualValue: 1,
    trend: "flat",
    lastUpdated: "2026-03-05",
    line1Owner: "Operations Manager",
    line2Owner: "Operational Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "or-2",
    categoryId: "operational-risk",
    sequence: 2,
    metric: "SLA Breach Rate",
    comparator: "max",
    unit: "percent",
    thresholds: {
      level1: { value: 3, label: "3%" },
      level2: { value: 5, label: "5%" },
      level3: { value: 7, label: "7%" },
      level4: { value: 10, label: "10%" },
    },
    actualValue: 4.2,
    trend: "up",
    lastUpdated: "2026-03-04",
    line1Owner: "Operations Manager",
    line2Owner: "Operational Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "or-3",
    categoryId: "operational-risk",
    sequence: 3,
    metric: "RCSA Actions Overdue",
    comparator: "max",
    unit: "count",
    thresholds: {
      level1: { value: 5, label: "5" },
      level2: { value: 8, label: "8" },
      level3: { value: 12, label: "12" },
      level4: { value: 15, label: "15" },
    },
    actualValue: 6,
    trend: "up",
    lastUpdated: "2026-03-03",
    line1Owner: "Operations Manager",
    line2Owner: "Operational Risk Oversight",
    line3Assurance: "Internal Audit",
  },
  {
    id: "comp-1",
    categoryId: "compliance-risk",
    sequence: 1,
    metric: "Regulatory Breaches",
    comparator: "max",
    unit: "count",
    thresholds: {
      level1: { value: 1, label: "1" },
      level2: { value: 2, label: "2" },
      level3: { value: 3, label: "3" },
      level4: { value: 4, label: "4" },
    },
    actualValue: 0,
    trend: "flat",
    lastUpdated: "2026-03-05",
    line1Owner: "Compliance Officer",
    line2Owner: "Head of Compliance",
    line3Assurance: "Internal Audit",
  },
  {
    id: "comp-2",
    categoryId: "compliance-risk",
    sequence: 2,
    metric: "Mandatory Training Completion",
    comparator: "min",
    unit: "percent",
    thresholds: {
      level1: { value: 98, label: "98%" },
      level2: { value: 95, label: "95%" },
      level3: { value: 92, label: "92%" },
      level4: { value: 90, label: "90%" },
    },
    actualValue: 96,
    trend: "down",
    lastUpdated: "2026-03-05",
    line1Owner: "Compliance Officer",
    line2Owner: "Head of Compliance",
    line3Assurance: "Internal Audit",
  },
  {
    id: "comp-3",
    categoryId: "compliance-risk",
    sequence: 3,
    metric: "Policy Breach Incidents",
    comparator: "max",
    unit: "count",
    thresholds: {
      level1: { value: 3, label: "3" },
      level2: { value: 5, label: "5" },
      level3: { value: 7, label: "7" },
      level4: { value: 10, label: "10" },
    },
    actualValue: 2,
    trend: "flat",
    lastUpdated: "2026-03-04",
    line1Owner: "Compliance Officer",
    line2Owner: "Head of Compliance",
    line3Assurance: "Internal Audit",
  },
];

export const INITIAL_OBSERVATIONS: RiskAppetiteObservation[] = [
  {
    id: "obs-1",
    metricId: "bsmr-3",
    previousValue: 126,
    newValue: 121,
    comment: "LCR decreased after quarter-end funding mix update.",
    updatedBy: "A. Patel (2LoD)",
    updatedAt: "2026-03-05T09:15:00Z",
  },
  {
    id: "obs-2",
    metricId: "bsmr-6",
    previousValue: 31,
    newValue: 33,
    comment: "Wholesale concentration increased during planned funding window.",
    updatedBy: "J. Rahman (1LoD)",
    updatedAt: "2026-03-05T12:00:00Z",
  },
];

export const INITIAL_EXCEPTIONS: RiskAppetiteException[] = [
  {
    id: "exc-1",
    metricId: "bsmr-3",
    reason: "Temporary liquidity profile transition approved pending month-end normalization.",
    approvedBy: "Chief Risk Officer",
    expiresAt: "2026-04-15",
    createdAt: "2026-03-05",
    status: "Open",
  },
];

export const INITIAL_WORKFLOW_EVENTS: RiskAppetiteWorkflowEvent[] = [
  {
    id: "wf-1",
    action: "created",
    actor: "Risk Analytics Team",
    at: "2026-03-01T08:00:00Z",
    comment: "Draft static RMF-aligned dashboard prepared for steering committee walkthrough.",
  },
];

